import React, { useRef, useState } from 'react';
import { AudioContainer, PlayButton, SeekBar, TimeDisplay, IconButton } from './styles';
import { ReactComponent as PlayIcon } from '../../svg/audio-play.svg';
import { ReactComponent as PauseIcon } from '../../svg/audio-pause.svg';
import { ReactComponent as VolumeIcon } from '../../svg/audio-volume.svg';
import { ReactComponent as MuteIcon } from '../../svg/audio-mute.svg';
import { formatTime } from 'components/Chatbot/helpers/utils';
import utils from 'utils';

interface AudioProps {
  mimeType: string;
  url?: string;
  audioBase64?: string;
}

export const Audio: React.FC<AudioProps> = ({ mimeType, url, audioBase64 }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const isAudio = mimeType?.startsWith('audio/');
  const src = audioBase64 ? `data:${mimeType};base64,${audioBase64}` : url;

  const handleTogglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const handleToggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const value = Number(e.target.value);
    audio.currentTime = value;
    setCurrentTime(value);
  };

  if (!isAudio || !src) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <AudioContainer>
      <audio
        ref={audioRef}
        src={src}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
      />
      <PlayButton
        isPlaying={isPlaying}
        onClick={handleTogglePlay}
        title={isPlaying ? utils.app.textTranslated('Pause') : utils.app.textTranslated('Play')}
      >
        {isPlaying ? <PauseIcon width={14} height={14} /> : <PlayIcon width={14} height={14} />}
      </PlayButton>
      <TimeDisplay>
        {formatTime(currentTime)} / {formatTime(duration)}
      </TimeDisplay>
      <SeekBar
        type="range"
        min={0}
        max={duration || 0}
        step={0.01}
        value={currentTime}
        progress={progress}
        onChange={handleSeek}
      />
      <IconButton
        title={isMuted ? utils.app.textTranslated('Unmute') : utils.app.textTranslated('Mute')}
        onClick={handleToggleMute}
      >
        {isMuted ? <MuteIcon width={16} height={16} /> : <VolumeIcon width={16} height={16} />}
      </IconButton>
    </AudioContainer>
  );
};
